<template>
  <!-- MCP section: copyable prompts and downloadable skill packs, side by side.
  Connect steps live in ConnectMCPModal, opened from the task row. Phones get
  one stacked column with labelled, full-width actions. -->
  <div class="grid grid-cols-1 lg:grid-cols-2">
    <!-- Prompts to Try -->
    <div
      class="border-b lg:border-b-0 lg:border-r border-border-normal"
      :class="phone ? 'p-4' : 'p-5'"
    >
      <div class="px-0.5">
        <BaseText
          type="label"
          size="sm"
          class="text-text-loud"
        >
          Prompts to Try
        </BaseText>
        <BaseText
          v-if="mcpConnected"
          type="body"
          size="xs"
          class="text-text-subdued mt-0.5"
        >
          Paste one into Claude or ChatGPT.
        </BaseText>
        <div
          v-else
          class="flex items-center gap-1.5 mt-0.5"
        >
          <InfoSimpleIcon
            class="flex-shrink-0 text-icon-normal"
            :width="16"
            :height="16"
          />
          <BaseText
            type="body"
            size="xs"
            class="text-text-normal"
          >
            {{ notConnectedNote }}
          </BaseText>
        </div>
      </div>
      <div class="mt-2.5 rounded-xl border border-border-normal bg-white divide-y divide-border-normal overflow-hidden">
        <div
          v-for="prompt in prompts"
          :key="prompt.id"
        >
          <div
            class="flex gap-3 pl-3 pr-2.5 py-2.5"
            :class="phone ? 'flex-col items-stretch' : 'items-center'"
          >
            <button
              type="button"
              class="group min-w-0 flex-grow flex items-start gap-2 text-left"
              :aria-expanded="expandedPromptId === prompt.id"
              @click="togglePrompt(prompt.id)"
            >
              <ChevronIcon
                :width="16"
                :height="16"
                class-name="flex-shrink-0 mt-0.5 text-icon-normal transform transition-transform duration-150"
                :class="expandedPromptId === prompt.id ? '' : '-rotate-90'"
              />
              <div class="min-w-0">
                <BaseText
                  type="label"
                  size="sm"
                  class="text-text-loud"
                >
                  {{ prompt.title }}
                </BaseText>
                <BaseText
                  type="body"
                  size="xs"
                  class="text-text-normal mt-0.5"
                >
                  {{ prompt.oneLiner }}
                </BaseText>
              </div>
            </button>
            <BaseButton
              outlined
              :class="phone ? 'w-full' : 'flex-shrink-0'"
              @click="copyPrompt(prompt)"
            >
              <span class="flex items-center gap-1.5">
                <StaticCopyIcon
                  class="flex-shrink-0 text-icon-normal"
                  :width="16"
                  :height="16"
                />
                Copy Prompt
              </span>
            </BaseButton>
          </div>
          <BaseHeightTransition>
            <div
              v-if="expandedPromptId === prompt.id"
              class="overflow-hidden"
            >
              <div
                class="pr-3 pb-3"
                :class="phone ? 'pl-3' : 'pl-9'"
              >
                <div class="rounded-lg border border-border-normal bg-neutral-10 px-3 py-2.5">
                  <BaseText
                    type="body"
                    size="sm"
                    class="text-text-muted whitespace-pre-wrap break-words"
                  >
                    {{ prompt.prompt }}
                  </BaseText>
                </div>
                <BaseText
                  type="body"
                  size="xs"
                  class="text-text-subdued mt-2"
                >
                  {{ prompt.wow }}
                </BaseText>
              </div>
            </div>
          </BaseHeightTransition>
        </div>
      </div>
    </div>

    <!-- Skill Packs -->
    <div :class="phone ? 'p-4' : 'p-5'">
      <div class="flex items-end justify-between gap-3 px-0.5">
        <div class="min-w-0">
          <BaseText
            type="label"
            size="sm"
            class="text-text-loud"
          >
            Skill Packs
          </BaseText>
          <BaseText
            type="body"
            size="xs"
            class="text-text-subdued mt-0.5"
          >
            Ready-made workflows your AI runs end to end. Download once, reuse every week.
          </BaseText>
        </div>
        <BaseText
          type="label"
          size="xs"
          class="text-text-subdued flex-shrink-0"
        >
          {{ skills.length }} skills
        </BaseText>
      </div>

      <div class="mt-2.5 rounded-xl border border-border-normal bg-white divide-y divide-border-normal overflow-hidden">
        <div
          v-for="skill in skills"
          :key="skill.id"
          class="flex items-center gap-3 pl-3 pr-2.5 py-2.5"
          :class="phone ? 'flex-wrap' : ''"
        >
          <div class="w-8 h-8 flex-shrink-0 self-start mt-0.5 rounded-lg bg-background-loud flex items-center justify-center">
            <MCPIcon
              :width="16"
              :height="16"
              class="text-white"
            />
          </div>
          <div
            class="min-w-0 flex-grow"
            :class="phone ? 'flex-1' : ''"
          >
            <BaseText
              type="label"
              size="sm"
              class="text-text-loud"
            >
              {{ skill.title }}
            </BaseText>
            <BaseText
              type="body"
              size="xs"
              class="text-text-normal mt-0.5"
            >
              {{ skill.oneLiner }}
            </BaseText>
            <div class="flex flex-wrap items-center gap-1 mt-1.5">
              <span
                v-for="need in skill.needs"
                :key="`${skill.id}-need-${need}`"
                class="flex items-center gap-1.5 pl-1.5 pr-2 py-0.5 rounded-md bg-background-normal"
              >
                <span
                  class="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  :class="productDotClass(need)"
                />
                <BaseText
                  type="label"
                  size="xs"
                  class="text-text-muted"
                >
                  {{ need }}
                </BaseText>
              </span>
            </div>
          </div>
          <!-- Phone: no hover tooltips, so the actions get labels -->
          <div
            v-if="phone"
            class="w-full flex flex-col gap-1.5"
          >
            <BaseButton
              primary
              forced-theme="black"
              class="w-full"
              @click="downloadSkill(skill)"
            >
              <span class="flex items-center gap-1.5">
                <DownloadIcon
                  class="flex-shrink-0"
                  :width="16"
                  :height="16"
                />
                Download
              </span>
            </BaseButton>
            <BaseButton
              outlined
              class="w-full"
              @click="copyActivation(skill)"
            >
              <span class="flex items-center gap-1.5">
                <StaticCopyIcon
                  class="flex-shrink-0 text-icon-normal"
                  :width="16"
                  :height="16"
                />
                Copy Prompt
              </span>
            </BaseButton>
          </div>
          <div
            v-else
            class="flex-shrink-0 flex items-center gap-1.5"
          >
            <BaseTooltipV2
              text="Download SKILL.md"
              position="top"
              alignment="end"
            >
              <BaseButton
                primary
                forced-theme="black"
                icon-only
                @click="downloadSkill(skill)"
              >
                <DownloadIcon
                  :width="16"
                  :height="16"
                />
              </BaseButton>
            </BaseTooltipV2>
            <BaseTooltipV2
              text="Copy activation prompt"
              position="top"
              alignment="end"
            >
              <BaseButton
                outlined
                icon-only
                @click="copyActivation(skill)"
              >
                <StaticCopyIcon
                  class="text-icon-normal"
                  :width="16"
                  :height="16"
                />
              </BaseButton>
            </BaseTooltipV2>
          </div>
        </div>
      </div>

      <!-- One install guide for the picked client -->
      <div class="mt-2 rounded-xl border border-border-normal bg-white">
        <button
          type="button"
          class="w-full flex items-center gap-2 px-3 py-2.5 text-left"
          :aria-expanded="showInstallGuide"
          @click="showInstallGuide = !showInstallGuide"
        >
          <ChevronIcon
            :width="16"
            :height="16"
            class-name="flex-shrink-0 text-icon-normal transform transition-transform duration-150"
            :class="showInstallGuide ? '' : '-rotate-90'"
          />
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted"
          >
            How to install skills
          </BaseText>
        </button>
        <BaseHeightTransition>
          <div
            v-if="showInstallGuide"
            class="overflow-hidden"
          >
            <McpClientSwitcher
              class="mb-3"
              :class="phone ? 'ml-3' : 'ml-9'"
              :value="selectedClientId"
              :clients="switcherClients"
              @input="selectClient"
            />
            <ol
              class="flex flex-col gap-2 pr-3 pb-3"
              :class="phone ? 'pl-3' : 'pl-9'"
            >
              <li
                v-for="(step, index) in installSteps"
                :key="`install-${selectedClientId}-${index}`"
                class="flex items-start gap-2"
              >
                <BaseText
                  type="label"
                  size="xs"
                  class="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-md border border-border-normal text-text-normal"
                >
                  {{ index + 1 }}
                </BaseText>
                <BaseText
                  type="body"
                  size="sm"
                  class="text-text-normal"
                >
                  {{ step }}
                </BaseText>
              </li>
            </ol>
          </div>
        </BaseHeightTransition>
      </div>
    </div>
  </div>
</template>

<script>
import posthog from 'posthog-js'
import { copyTextAsync } from '@/utils/clipboard'
import { MCP_CLIENTS, MCP_PROMPTS, MCP_PROMPTS_NOT_CONNECTED_NOTE } from '@/utils/setup/setupContent'
import { MCP_SKILLS, MCP_SKILL_INSTALL } from '@/utils/setup/mcpSkills'
import McpClientSwitcher from '@/components/api/mcp/McpClientSwitcher.vue'
import MCPIcon from '@/components/globals/Icons/ApiIcons/MCPIcon.vue'
import StaticCopyIcon from '@/components/globals/Icons/StaticCopyIcon.vue'
import DownloadIcon from '@/components/globals/Icons/DownloadIcon.vue'
import ChevronIcon from '@/components/globals/Icons/ChevronIcon.vue'
import InfoSimpleIcon from '@/components/globals/Icons/InfoSimpleIcon.vue'

// Literal class strings so Tailwind's purge keeps them
const PRODUCT_DOT_CLASSES = {
  Spyder: 'bg-primary-red-100',
  Lens: 'bg-background-loud',
  Discovery: 'bg-primary-purple-100',
  Board: 'bg-primary-blue-100'
}

export default {
  name: 'SetupMcpPanel',
  components: {
    McpClientSwitcher,
    MCPIcon,
    StaticCopyIcon,
    DownloadIcon,
    ChevronIcon,
    InfoSimpleIcon
  },
  props: {
    // mcp_connected step is done; hides the "connect first" note
    mcpConnected: {
      type: Boolean,
      default: false
    },
    phone: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      clients: MCP_CLIENTS,
      prompts: MCP_PROMPTS,
      notConnectedNote: MCP_PROMPTS_NOT_CONNECTED_NOTE,
      skills: MCP_SKILLS,
      selectedClientId: MCP_CLIENTS[0].id,
      expandedPromptId: null,
      showInstallGuide: false
    }
  },
  computed: {
    switcherClients () {
      return this.clients.map((client) => ({ key: client.id, label: client.label }))
    },
    installSteps () {
      const pack = MCP_SKILL_INSTALL[this.selectedClientId] || MCP_SKILL_INSTALL.claude
      return (pack && pack.steps) || []
    }
  },
  methods: {
    productDotClass (product) {
      return PRODUCT_DOT_CLASSES[product] || 'bg-icon-disabled'
    },
    selectClient (clientId) {
      this.selectedClientId = clientId
      posthog.capture('Setup MCP Client Selected', { client: clientId })
    },
    togglePrompt (id) {
      this.expandedPromptId = this.expandedPromptId === id ? null : id
    },
    async copyPrompt (prompt) {
      try {
        await copyTextAsync(prompt.prompt)
        this.$showAlert({ type: 'success', message: 'Prompt copied' })
        posthog.capture('Setup Prompt Copied', { prompt_id: prompt.id })
      } catch (error) {
        this.$showAlert({ type: 'error', message: 'Unable to copy to clipboard' })
      }
    },
    async copyActivation (skill) {
      try {
        await copyTextAsync(skill.activationPrompt)
        this.$showAlert({ type: 'success', message: 'Activation prompt copied' })
        posthog.capture('Setup Skill Activation Copied', { skill_id: skill.id })
      } catch (error) {
        this.$showAlert({ type: 'error', message: 'Unable to copy to clipboard' })
      }
    },
    downloadSkill (skill) {
      const path = skill.downloadPath || `/mcp-skills/${skill.id}/SKILL.md`
      const link = document.createElement('a')
      link.href = path
      link.download = `${skill.id}-SKILL.md`
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      posthog.capture('Setup Skill Downloaded', { skill_id: skill.id })
    }
  }
}
</script>
